// noinspection DuplicatedCode
import React, { useEffect, useState } from "react";
import "react-datepicker/dist/react-datepicker.css";

import { Colors } from "../../../assets/Colors";
import { Card } from "@material-tailwind/react";
import { HrdInputGenerator } from "../../../components/form/HrdInputGenerator";
import { useForm } from "../../../components/isRequired";
import { useMutation } from "@tanstack/react-query";
import { fetchPostSilance } from "../../../fetchers";
import { toast } from "react-toastify";
import { BukuNeraca, buildNeracaRows } from "../../../components/tablecard/DynamicTable";
import { BsFileEarmarkExcelFill, BsFileEarmarkPdfFill } from "react-icons/bs";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
// react-export-table-to-excel tak mendukung colspan/text-align, jadi judul
// (2 baris rata tengah — sama seperti tampilan layar & halaman Laba Rugi)
// dibuat lewat HTML table manual di bawah, bukan lewat library itu.
const escapeHtml = (v) =>
  String(v).replace(
    /[&<>"']/g,
    (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c])
  );

const downloadExcelCentered = ({ fileName, sheet, colCount, titleRows, headerRow, bodyRows }) => {
  const titleHtml = titleRows
    .map(
      (t) =>
        `<tr><th colspan="${colCount}" style="text-align:center;">${escapeHtml(t)}</th></tr>`
    )
    .join("");
  const spacerHtml = `<tr>${Array(colCount).fill("<td></td>").join("")}</tr>`;
  const headerHtml = `<tr>${headerRow.map((h) => `<th>${escapeHtml(h)}</th>`).join("")}</tr>`;
  const bodyHtml = bodyRows
    .map((row) => `<tr>${row.map((v) => `<td>${escapeHtml(v)}</td>`).join("")}</tr>`)
    .join("");
  const table = `<table><tbody>${titleHtml}${spacerHtml}${headerHtml}${bodyHtml}</tbody></table>`;
  const html =
    '<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">' +
    `<head><meta charset="UTF-8"><!--[if gte mso 9]><xml><x:ExcelWorkbook><x:ExcelWorksheets><x:ExcelWorksheet><x:Name>${escapeHtml(
      sheet
    )}</x:Name><x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions></x:ExcelWorksheet></x:ExcelWorksheets></x:ExcelWorkbook></xml><![endif]--></head>` +
    `<body>${table}</body></html>`;
  const element = document.createElement("a");
  element.href = "data:application/vnd.ms-excel;base64," + window.btoa(unescape(encodeURIComponent(html)));
  element.download = `${fileName}.xls`;
  document.body.appendChild(element);
  element.click();
  document.body.removeChild(element);
};

// Kelas tombol export — sama gaya dgn report Laba Rugi / Monitoring AP (h-10
// agar sejajar dgn tombol "Buka").
const BTN_BASE =
  "h-10 px-3 text-sm font-medium rounded-lg inline-flex items-center justify-center gap-1.5 whitespace-nowrap transition-colors disabled:opacity-50 disabled:cursor-not-allowed mr-2 mb-2";

// Nominal export: bilangan bulat berpemisah ribuan tanpa "Rp"; negatif dalam
// tanda kurung, mengikuti konvensi tampilan tabel Neraca.
const fmtExportNominal = (v) => {
  const n = parseFloat(v || 0);
  const s = Math.abs(n).toLocaleString("id-ID", { maximumFractionDigits: 2 });
  return n < 0 ? "(" + s + ")" : s;
};
const fmtExportPct = (v) =>
  parseFloat(v || 0).toLocaleString("id-ID", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }) + "%";

// Baris export dari buildNeracaRows: header jadi baris judul, spacer jadi
// baris kosong, sisanya (data/Total Group/Total Main Account/Total Passiva)
// jadi baris [Uraian, Bulan Berjalan, Bulan Sebelumnya, Naik/(Turun), %] —
// urutan kolom sama dgn tabel di layar.
const buildExportBody = (rows) =>
  rows.map((r) => {
    if (r.type === "header") return [r.label.toUpperCase(), "", "", "", ""];
    if (r.type === "spacer") return ["", "", "", "", ""];
    return [
      r.label,
      fmtExportNominal(r.current),
      fmtExportNominal(r.previous),
      fmtExportNominal(r.change),
      fmtExportPct(r.pct),
    ];
  });

const ReportNeraca = ({}) => {
  let initialState = {
    Bulan: new Date(),
    BisnisUnitID: "U0002",
  };

  const validations = [];

  const { values, errors, touched, customHandler } = useForm(
    initialState,
    validations
  );

  const mutationData = useMutation((param) =>
    fetchPostSilance("Accounting/Neraca", param)
  );
  const [param, setParam] = useState({
    start: 0,
    count: 0,
    search: "",
    BisnisUnitID: "U0002",
    Bulan: (new Date().getMonth() + 1).toString(),
    Tahun: new Date().getFullYear().toString(),
  });
  useEffect(() => {
    toast.info("Loading table!", {
      position: "top-center",
      autoClose: 1000,
      hideProgressBar: true,
      closeOnClick: true,
      pauseOnHover: false,
      draggable: true,
      theme: "light",
    });

    mutationData.mutate(param);
  }, [param]);

  const header = [
    { name: "Aktiva Lancar", key: "NamaKaryawan", modifiers: "Text" },
    { name: "Nominal", key: "NamaKaryawan", modifiers: "Text" },
  ];

  const filterApi = () => {
    setParam({
      start: 0,
      count: 0,
      search: "",
      BisnisUnitID: values.BisnisUnitID,
      Bulan: (new Date(values.Bulan).getMonth() + 1).toString(),
      Tahun: new Date(values.Bulan).getFullYear().toString(),
    });
  };

  const exportFileName = () =>
    `Neraca_${param.BisnisUnitID}_${param.Bulan}_${param.Tahun}`;

  const handleDownloadExcel = () => {
    const payload = mutationData.data && mutationData.data.data;
    if (!payload) {
      toast.warn("Tidak ada data untuk di-export", {
        position: "top-center",
        autoClose: 2000,
      });
      return;
    }
    const { rows, currentPeriod, previousPeriod } = buildNeracaRows(payload);
    try {
      // Judul 2 baris rata tengah (colspan penuh), sama seperti tampilan layar.
      downloadExcelCentered({
        fileName: exportFileName(),
        sheet: "Neraca",
        colCount: 5,
        titleRows: ["PT RANJI KARYA SAKTI", "NERACA"],
        headerRow: ["Uraian", currentPeriod, previousPeriod, "Naik / (Turun)", "%"],
        bodyRows: buildExportBody(rows),
      });
      toast.success("Berhasil export ke Excel", {
        position: "top-center",
        autoClose: 2000,
      });
    } catch (error) {
      toast.error("Gagal export Excel: " + (error.message || "Unknown error"), {
        position: "top-center",
        autoClose: 3000,
      });
    }
  };

  const handleDownloadPDF = () => {
    const payload = mutationData.data && mutationData.data.data;
    if (!payload) {
      toast.warn("Tidak ada data untuk di-export", {
        position: "top-center",
        autoClose: 2000,
      });
      return;
    }
    const { rows, currentPeriod, previousPeriod } = buildNeracaRows(payload);
    try {
      const doc = new jsPDF({ orientation: "landscape" });
      const pageWidth = doc.internal.pageSize.getWidth();
      // Judul 2 baris rata tengah, sama seperti tampilan layar.
      doc.setFontSize(14);
      doc.text("PT RANJI KARYA SAKTI", pageWidth / 2, 12, { align: "center" });
      doc.text("NERACA", pageWidth / 2, 19, { align: "center" });

      const header = ["Uraian", currentPeriod, previousPeriod, "Naik / (Turun)", "%"];
      const body = buildExportBody(rows);
      autoTable(doc, {
        startY: 25,
        head: [header],
        body,
        styles: { fontSize: 8, cellPadding: 2 },
        headStyles: { fillColor: [30, 58, 95], textColor: 255, fontStyle: "bold" },
        didParseCell: (data) => {
          if (data.column.index >= 1) data.cell.styles.halign = "right";
        },
      });

      doc.save(`${exportFileName()}.pdf`);
      toast.success("Berhasil export ke PDF", {
        position: "top-center",
        autoClose: 2000,
      });
    } catch (error) {
      toast.error("Gagal export PDF: " + (error.message || "Unknown error"), {
        position: "top-center",
        autoClose: 3000,
      });
    }
  };

  return (
    <>
      <div className="md:ml-64">
        <div
          style={{ background: Colors.primary }}
          className="pt-14 pb-16 px-3 md:px-8 h-auto"
        >
          <div className="container mx-auto max-w-full">
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4"></div>
          </div>
        </div>
        <div className="px-3 md:px-8 h-auto -mt-32">
          <div className="container mx-auto max-w-full">
            <div className="grid grid-cols-1 px-4">
              <Card>
                <div className="flex flex-row flex-wrap items-start justify-start w-full px-2 py-2">
                  <div className={"w-1/4"}>
                    <HrdInputGenerator
                      type={"BisnisUnit"}
                      name={"BisnisUnitID"}
                      title={"BisnisUnit"}
                      value={values}
                      required={true}
                      includeAll={true}
                      onChange={customHandler}
                      touched={touched}
                      errors={errors}
                      size={"full"}
                    />
                  </div>
                  <div className={"w-1/4"}>
                    <HrdInputGenerator
                      type={"bulan"}
                      name={"Bulan"}
                      title={"Bulan"}
                      value={values}
                      required={true}
                      onChange={customHandler}
                      touched={touched}
                      errors={errors}
                      size={"full"}
                    />
                  </div>

                  <div className={"w-1/4"}>
                    <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                      Buka data
                    </label>

                    <button
                      type="button"
                      onClick={filterApi}
                      className=" text-main-100 hover:text-white border border-main-100 hover:bg-main-100 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2 text-center mr-2 mb-2 "
                    >
                      Buka
                    </button>

                    <button
                      type="button"
                      onClick={handleDownloadExcel}
                      disabled={!mutationData.data}
                      title="Download tabel ke Excel"
                      className={`${BTN_BASE} bg-white text-green-700 border border-green-600 hover:bg-green-600 hover:text-white`}
                    >
                      <BsFileEarmarkExcelFill />
                      Excel
                    </button>

                    <button
                      type="button"
                      onClick={handleDownloadPDF}
                      disabled={!mutationData.data}
                      title="Download tabel ke PDF"
                      className={`${BTN_BASE} bg-white text-red-600 border border-red-500 hover:bg-red-500 hover:text-white`}
                    >
                      <BsFileEarmarkPdfFill />
                      PDF
                    </button>
                  </div>
                </div>

                <div className="flex flex-col items-center content-center ">
                  <div className="w-full text-center pt-4">
                    <p className="font-bold text-lg">PT RANJI KARYA SAKTI</p>
                    <p className="font-bold">NERACA</p>
                  </div>
                  <BukuNeraca
                    data={mutationData.data}
                    header={header}
                    name={"Nama"}
                    title={"Report Jurnal"}
                  />
                </div>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ReportNeraca;
